import type { ReactNode } from "react";
import { expandEmoticons } from "@/lib/emoticons";
import { findMentionRanges } from "@/lib/chatMentions";
import { isCallLogBody } from "@/lib/chatFormat";
import { loneMessageEmojis, notoAnimSrc } from "@/lib/chatNotoEmoji";

/** URLs http(s) dans un message — ponctuation finale laissée hors lien. */
const URL_RE = /https?:\/\/[^\s<>"'`]+/gi;

function trimUrlEdge(raw: string): { href: string; trailing: string } {
  let href = raw;
  let trailing = "";
  while (href.length > 0 && /[.,;:!?)]+$/.test(href)) {
    trailing = href.slice(-1) + trailing;
    href = href.slice(0, -1);
  }
  return { href, trailing };
}

function linkifySegment(source: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  const re = new RegExp(URL_RE.source, "gi");
  let match: RegExpExecArray | null;
  while ((match = re.exec(source)) !== null) {
    if (match.index > last) {
      nodes.push(source.slice(last, match.index));
    }
    const { href, trailing } = trimUrlEdge(match[0]);
    if (href) {
      nodes.push(
        <a
          key={`${keyPrefix}-link-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-msg-link"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {href}
        </a>
      );
    }
    if (trailing) nodes.push(trailing);
    last = match.index + match[0].length;
  }
  if (last < source.length) nodes.push(source.slice(last));
  return nodes.length > 0 ? nodes : [source];
}

export function linkifyMessageText(text: string, mentionLabels: string[] = []): ReactNode[] {
  const source = expandEmoticons(text);
  const ranges = findMentionRanges(source, mentionLabels);
  if (ranges.length === 0) {
    return linkifySegment(source, "t");
  }
  const nodes: ReactNode[] = [];
  let last = 0;
  ranges.forEach((range, i) => {
    if (range.start > last) {
      nodes.push(...linkifySegment(source.slice(last, range.start), `s${i}`));
    }
    nodes.push(
      <span key={`mention-${i}`} className="chat-mention">
        {source.slice(range.start, range.end)}
      </span>
    );
    last = range.end;
  });
  if (last < source.length) {
    nodes.push(...linkifySegment(source.slice(last), "e"));
  }
  return nodes.length > 0 ? nodes : [source];
}

type Props = {
  text: string;
  mentionLabels?: string[];
  className?: string;
};

export function ChatMessageText({ text, mentionLabels = [], className = "" }: Props) {
  const lone = !isCallLogBody(text) ? loneMessageEmojis(text) : null;
  if (lone) {
    return (
      <span className={`chat-msg-jumbo${lone.length > 1 ? " chat-msg-jumbo--row" : ""} ${className}`.trim()}>
        {lone.map((e, i) => {
          const src = notoAnimSrc(e);
          return src ? (
            <img
              key={`je-${i}`}
              className="chat-msg-jumbo__anim"
              src={src}
              alt={e}
              draggable={false}
            />
          ) : (
            <span key={`je-${i}`} className="chat-msg-jumbo__plain">
              {e}
            </span>
          );
        })}
      </span>
    );
  }
  return (
    <p className={`chat-msg-text whitespace-pre-wrap break-words ${className}`.trim()}>
      {linkifyMessageText(text, mentionLabels)}
    </p>
  );
}
